/**
 * YOUR DATA — the section that did not exist.
 *
 * The audit's grep found no account deletion, no account closure and no data
 * export anywhere in this product, frontend or backend. For software that holds
 * a person's name, phone number, unit and a log of when they came and went,
 * "you cannot have a copy and you cannot leave" is a trust failure before it is
 * a compliance one — and both are ordinary expectations of any account screen.
 *
 * ── WHY CLOSING IS A DEACTIVATION AND SAYS SO ──
 * Entries this person wrote at a front desk are the BUILDING's record of who
 * came and went. Other people rely on them and this person does not own them,
 * so erasing the author would silently rewrite somebody else's history. The
 * account stops working completely and immediately; the entries keep their
 * author. The copy states that plainly rather than implying a scrub that is not
 * going to happen — an overstated promise here is worse than an honest limit.
 *
 * ── THE SOLE-OWNER CASE ──
 * The last owner of an organisation is the only account that can manage
 * billing, staff and buildings. The server refuses that close with
 * `code: 'SOLE_OWNER'` and an explanation, and this screen shows it as written
 * rather than replacing it with a generic failure.
 */
import React, { useState } from 'react';
import axios from 'axios';
import styled from '@emotion/styled';
import { apiRoute } from '../../../constants/api-constants';
import { useAuth } from '../../../context/auth-provider';
import toast from '../../../utils/toast';
import { AppButton } from '../../button/app-button';
import { ConfirmDialog } from '../../modal/confirm-dialog';
import { SectionHeading, SectionLead } from './account.styled';
import {
  palette,
  radius,
  spacing,
  status,
  typography,
} from '../../../style-utils/theme';

const Block = styled.section`
  margin-top: ${spacing.lg};
  padding: ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  background: ${palette.surface};
`;

const BlockTitle = styled.h3`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
`;

const BlockText = styled.p`
  margin: 0 0 ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};
`;

const Refusal = styled.p`
  margin: ${spacing.sm} 0 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${status.alert.fg};
`;

export const AccountData: React.FC = () => {
  const { logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  /**
   * Fetched with axios and saved from a blob rather than pointed at with a
   * plain link: the route is cookie-authenticated and returns JSON, and a bare
   * <a href> would open it in a tab for anyone whose browser renders JSON
   * inline. The blob keeps it a download in every browser.
   */
  const exportData = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const response = await axios.get(`${apiRoute}/profile/export`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.download = 'lobbero-my-data.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Your data has been downloaded.');
    } catch {
      toast.error('We could not prepare your data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const closeAccount = async () => {
    if (closing) return;
    setClosing(true);
    setRefusal(null);
    try {
      await axios.post(`${apiRoute}/profile/close`);
      setConfirmOpen(false);
      toast.success('Your account has been closed.');
      // Straight out: the session is already revoked server-side, so every
      // subsequent request would fail anyway. Signing out locally is what makes
      // that orderly instead of a wall of errors.
      await logout();
    } catch (error) {
      const body = (
        error as { response?: { data?: { message?: string; code?: string } } }
      ).response?.data;

      setConfirmOpen(false);
      setRefusal(
        body?.message ||
          'We could not close your account. Please try again, or contact support.'
      );
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <SectionHeading>Your data</SectionHeading>
      <SectionLead>
        A copy of everything Lobbero holds about you personally, and the way out
        if you want one.
      </SectionLead>

      <Block>
        <BlockTitle>Download your data</BlockTitle>
        <BlockText>
          Your account details and your residency history, as a JSON file.
          Records you created at a front desk belong to the building and are not
          included.
        </BlockText>
        <AppButton
          variant="secondary"
          size="compact"
          loading={exporting}
          loadingLabel="Preparing your data"
          onClick={exportData}
        >
          Download my data
        </AppButton>
      </Block>

      <Block>
        <BlockTitle>Close your account</BlockTitle>
        <BlockText>
          Your account stops working immediately and every device is signed out.
          Entries you wrote at a front desk stay in the building&apos;s log with
          your name on them — they are the building&apos;s record of who came and
          went, and they are not ours to erase.
        </BlockText>
        <AppButton
          variant="destructive"
          size="compact"
          disabled={closing}
          onClick={() => setConfirmOpen(true)}
        >
          Close my account
        </AppButton>
        {refusal ? <Refusal role="alert">{refusal}</Refusal> : null}
      </Block>

      <ConfirmDialog
        open={confirmOpen}
        title="Close your account?"
        message="You will be signed out of every device and will not be able to sign in again. This cannot be undone from inside the app — an administrator would have to reactivate you."
        primaryLabel={closing ? 'Closing…' : 'Close my account'}
        primaryVariant="destructive"
        dismissLabel="Keep my account"
        onPrimary={closeAccount}
        onDismiss={() => setConfirmOpen(false)}
      />
    </>
  );
};
